import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import axios from '../../dataClient/dataClient'
import { formatUsersToAssignDropdownOptions } from '../../util/userFormatting'
import { attachmentStatusDropdownOptions } from '../../util/attachmentConsts'
import { FlexColumn, FlexEndContainer, InputWrapperDiv, SpaceBetweenRowContainer } from '../styles/Containers'
import BodyText from '../styles/Text'
import Dropdown from '../inputFields/Dropdown'
import RichTextReadOnly from '../inputFields/RichTextReadOnly'
import AttachmentReadOnlyHeading from '../foiAttachments/AttachmentReadOnlyHeading'
import LetterTemplateHeader from '../letterTemplates/LetterTemplateHeader'

const DraftHeaderActions = styled(FlexEndContainer)`
  flex-wrap: wrap;
`

function getDraftStatusValue(letter) {
  return letter.status || 'DRAFTING'
}

function getUser(user, users) {
  if (!user) return undefined
  if (typeof user === 'object') return user

  const matchingUser = users.find(u => u.id === user)
  return matchingUser ? matchingUser : undefined
}

function getUserId(user) {
  if (!user) return ''
  if (typeof user === 'object') return user.id || ''
  return user
}

function Letter({ state, letter, users = [], handleViewLetter = () => {} }) {
  const { id, body } = letter

  const isDraft = state === 'DRAFT'
  const assignedToOptions = formatUsersToAssignDropdownOptions(users)
  const assignedToValue = getUserId(letter.assignedTo)
  const selectedAssignedToValue = assignedToOptions.some(option => option.value === assignedToValue) ? assignedToValue : ''
  const draftStatusOptions = attachmentStatusDropdownOptions
  const draftStatusValue = getDraftStatusValue(letter)
  const selectedDraftStatusValue = draftStatusOptions.some(option => option.value === draftStatusValue) ? draftStatusValue : 'DRAFTING'

  const patchLetterAssignee = selected => {
    const updatedValue = selected !== '' ? selected : null
    return axios.patch(`/letter/${id}`, { assignedTo: updatedValue }).then(() => handleViewLetter(id, { refreshFoi: true }))
  }

  const patchLetterDraftStatus = selected => {
    return axios.patch(`/letter/${id}`, { status: selected }).then(() => handleViewLetter(id, { refreshFoi: true }))
  }

  return (
    <FlexColumn gap="15px">
      {state === 'TEMPLATE' ? (
        <AttachmentReadOnlyHeading state="TEMPLATE" attachment={letter} />
      ) : isDraft ? (
        <SpaceBetweenRowContainer gap="10px">
          <AttachmentReadOnlyHeading state="DRAFT_LETTER" attachment={{ ...letter, modifiedBy: getUser(letter.modifiedBy, users) }} />
          <DraftHeaderActions gap="10px">
            <InputWrapperDiv minwidth="210px">
              <Dropdown
                options={draftStatusOptions}
                defaultValue={selectedDraftStatusValue}
                heading="Status"
                updateHeading="Update Status"
                onSelectOption={patchLetterDraftStatus}
              />
            </InputWrapperDiv>
            <InputWrapperDiv minwidth="210px">
              <Dropdown
                options={assignedToOptions}
                defaultValue={selectedAssignedToValue}
                heading="Assigned To"
                updateHeading="Update Assignee"
                onSelectOption={patchLetterAssignee}
              />
            </InputWrapperDiv>
          </DraftHeaderActions>
        </SpaceBetweenRowContainer>
      ) : (
        <AttachmentReadOnlyHeading state="SENT_INTERNAL" attachment={{ ...letter, sentBy: getUser(letter.sentBy, users) }} />
      )}
      {state === 'TEMPLATE' && <LetterTemplateHeader />}
      <FlexColumn gap="5px">
        {state === 'TEMPLATE' && <BodyText>Letter template body text:</BodyText>}
        <RichTextReadOnly value={body} />
      </FlexColumn>
    </FlexColumn>
  )
}

const userSummaryPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  chipColor: PropTypes.string
})

Letter.propTypes = {
  state: PropTypes.oneOf(['SENT', 'DRAFT', 'TEMPLATE']).isRequired,
  letter: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    updatedAt: PropTypes.instanceOf(Date).isRequired,
    assignedTo: PropTypes.oneOfType([PropTypes.string, userSummaryPropType]),
    status: PropTypes.oneOf(['DRAFTING', 'AWAITING_APPROVAL', 'READY_TO_SEND']),
    sentAt: PropTypes.instanceOf(Date),
    sentBy: PropTypes.oneOfType([PropTypes.string, userSummaryPropType])
  }).isRequired,
  users: PropTypes.array,
  handleViewLetter: PropTypes.func
}

export default Letter
